'use client';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import { useStripeModal } from '@/zustand/store';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { getStripePaymentEndpoint } from '@/config/endpoints';
import { kazeFetch } from '@/utils/kazeFetch';
import { reportPaymentSuccess } from '@/posthog/reportPaymentSuccess';
import { useCallback } from 'react';
import { X } from 'lucide-react';

const stripePromise = loadStripe(
  process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!
);

interface GlobalStripeModalProps {
  preventInteractOutside?: boolean;
  customClassName?: string;
}

export default function GlobalStripeModal({
  preventInteractOutside = true,
  customClassName = '',
}: GlobalStripeModalProps) {
  const isStripeModalOpen = useStripeModal((s) => s.isStripeModalOpen);
  const setStripeModalOpen = useStripeModal((s) => s.setStripeModalOpen);
  const stripeModalContext = useStripeModal((s) => s.stripeModalContext);

  // 使用 useCallback 记忆回调函数
  const handleComplete = useCallback(async () => {
    try {
      const session_id = stripeModalContext.clientSecret.split('_secret_')[0];
      kazeFetch(getStripePaymentEndpoint, {
        method: 'POST',
        body: JSON.stringify({ session_id }),
      }).then((data) => {
        const transaction_id = data.charge_id;
        stripeModalContext.reportInfo.transaction_id = transaction_id;
        reportPaymentSuccess(stripeModalContext.reportInfo);
      });
    } catch (error) {
      console.error('Error report payment success:', error);
    } finally {
      setStripeModalOpen(false, stripeModalContext);
      stripeModalContext?.paymentSuccessCallback?.();
    }
  }, [stripeModalContext, setStripeModalOpen]);

  // 同样使用 useCallback 记忆 options 对象
  const checkoutOptions = useCallback(
    () => ({
      clientSecret: stripeModalContext.clientSecret,
      onComplete: handleComplete,
    }),
    [stripeModalContext.clientSecret, handleComplete]
  );

  return (
    <Dialog
      open={isStripeModalOpen}
      onOpenChange={(open) => setStripeModalOpen(open, stripeModalContext)}
    >
      <DialogContent
        className={`h-[100%] w-full max-w-none overflow-hidden !rounded-none bg-white p-5 ${customClassName}`}
        onInteractOutside={(e) => preventInteractOutside && e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        showCloseButton={false}
      >
        <DialogHeader className='h-0'>
          <DialogTitle className='hidden' />
          <DialogDescription className='hidden' />
        </DialogHeader>
        {stripeModalContext.clientSecret && (
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={checkoutOptions()}
          >
            <div className='flex h-full w-full overflow-y-auto'>
              <EmbeddedCheckout className='w-full' />
            </div>
          </EmbeddedCheckoutProvider>
        )}
        <DialogClose className='absolute right-2 top-2 z-30 rounded-md bg-white p-2 text-gray-800 shadow-sm duration-200 hover:bg-gray-100'>
          <X className='h-7 w-7' />
          <span className='sr-only'>Close</span>
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
}
